import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';
import { Button } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import {
  CUSTOMER_DISPLAY_EVENTS as events, CUSTOMER_DISPLAY_NAMESPACE,
  createCustomerDisplayOrderState, authorizeCustomerDisplayPublisher, acceptCustomerDisplaySnapshot,
  isCustomerDisplayId, getCustomerDisplayInitials, type CustomerDisplayBrand, type CustomerDisplaySnapshot
} from '@washday/utilities';
import { readDisplayBrand, saveDisplayBrand } from './brandStorage';
import './customerDisplay.css';

export default function CustomerDisplayViewer() {
  const { displayId } = useParams<{ displayId: string }>();
  return <CustomerDisplayScreen key={displayId} displayId={displayId} />;
}

function CustomerDisplayScreen({ displayId }: { displayId: string }) {
  const { t, i18n } = useTranslation('CustomerDisplay');
  const [snapshot, setSnapshot] = useState<CustomerDisplaySnapshot | null>(null);
  const [brand, setBrand] = useState<CustomerDisplayBrand | null>(() => readDisplayBrand(displayId));
  const [fullscreenError, setFullscreenError] = useState(false);
  const ordering = useRef(createCustomerDisplayOrderState());

  useEffect(() => {
    if (!isCustomerDisplayId(displayId)) return;
    ordering.current = createCustomerDisplayOrderState();
    let lastLocalPulse = 0;
    let remoteConnected = false;
    let remotePublisherId: string | null = null;
    let localPublisherId: string | null = null;
    let channel: BroadcastChannel | null = null;
    const receiveStatus = (input: unknown, source: 'local' | 'remote') => {
      const status = input as { connected?: boolean; publisherInstanceId?: string | null };
      if (typeof status?.connected !== 'boolean') return;
      const requestAfterRecovery = source === 'remote' && status.connected && (!remoteConnected || remotePublisherId !== status.publisherInstanceId);
      if (source === 'local' && remoteConnected && status.publisherInstanceId !== remotePublisherId) return;
      if (source === 'local') {
        if (status.connected) { lastLocalPulse = Date.now(); localPublisherId = status.publisherInstanceId || null; }
        else if (status.publisherInstanceId === localPublisherId) { lastLocalPulse = 0; localPublisherId = null; }
      } else {
        remoteConnected = status.connected;
        remotePublisherId = status.connected ? status.publisherInstanceId || null : null;
      }
      if (status.connected && typeof status.publisherInstanceId === 'string') {
        // A verified server ownership handshake supersedes provisional local fallback.
        if (source === 'remote' && ordering.current.retiredPublisherInstanceIds.includes(status.publisherInstanceId)) ordering.current = createCustomerDisplayOrderState();
        ordering.current = authorizeCustomerDisplayPublisher(ordering.current, status.publisherInstanceId);
      } else if (!remoteConnected && Date.now() - lastLocalPulse > 15_000) {
        ordering.current = authorizeCustomerDisplayPublisher(ordering.current, null);
      }
      setSnapshot(ordering.current.snapshot);
      if (requestAfterRecovery) socket.emit(events.request);
    };
    const receiveSnapshot = (input: unknown) => {
      const next = acceptCustomerDisplaySnapshot(ordering.current, input);
      ordering.current = next;
      if (next.snapshot) {
        setSnapshot(next.snapshot);
        setBrand(next.snapshot.brand);
        saveDisplayBrand(displayId, next.snapshot.brand);
      }
    };
    const socket = io(`${import.meta.env.REACT_APP_BACKEND_BASE_URL?.replace(/\/$/, '')}${CUSTOMER_DISPLAY_NAMESPACE}`, {
      transports: ['websocket'], forceNew: true
    });
    const request = () => socket.emit(events.subscribe, { displayId });
    socket.on('connect', request);
    socket.on(events.status, (value) => receiveStatus(value, 'remote'));
    socket.on(events.snapshot, receiveSnapshot);
    socket.on('disconnect', () => receiveStatus({ connected: false }, 'remote'));
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(`washday.customer-display.${displayId}`);
        channel.onmessage = (event) => {
          if (event.data?.type === events.status) receiveStatus(event.data, 'local');
          if (event.data?.type === events.snapshot) receiveSnapshot(event.data.snapshot);
        };
        channel.postMessage({ type: events.request });
      }
    } catch { /* Socket.IO handles browsers without BroadcastChannel. */ }
    const checkPresence = setInterval(() => {
      if (!remoteConnected && Date.now() - lastLocalPulse > 15_000) {
        ordering.current = authorizeCustomerDisplayPublisher(ordering.current, null);
        setSnapshot(null);
      }
    }, 5_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') { socket.emit(events.request); channel?.postMessage({ type: events.request }); }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { socket.disconnect(); channel?.close(); clearInterval(checkPresence); document.removeEventListener('visibilitychange', onVisible); };
  }, [displayId]);

  const valid = isCustomerDisplayId(displayId);
  const knownBrand = Boolean(brand?.name);
  const money = (amount: number) => new Intl.NumberFormat(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { style: 'currency', currency: snapshot?.currency || 'MXN' }).format(amount);
  const showingOrder = snapshot && !['idle', 'disconnected'].includes(snapshot.state);
  const totals = snapshot?.totals;
  // Compare formatted magnitudes so visibility follows the displayed currency's rounding, including -0.
  const breakdownKeys = (['subtotal', 'discount', 'taxes', 'shipping'] as const)
    .filter((key) => key === 'subtotal' || money(Math.abs(totals?.[key] || 0)) !== money(0));
  return <main className="customer-display" data-testid="customer-display" aria-live="polite">
    <header className="customer-display-header">
      <div className="customer-display-brand">
        {brand?.storeName && <span className="customer-display-branch">
          <IconMapPin size={17} stroke={1.5} aria-hidden="true" />
          <span>{t('branchLabel', { storeName: brand.storeName })}</span>
        </span>}
      </div>
      {document.fullscreenEnabled && <Button variant="subtle" size="xs" onClick={async () => {
        try { await document.documentElement.requestFullscreen(); } catch { setFullscreenError(true); }
      }}>{t('fullscreen')}</Button>}
    </header>
    {fullscreenError && <p role="status">{t('fullscreenFailed')}</p>}
    {showingOrder && knownBrand && <div className="customer-display-brand">
      <BrandMark key={brand?.logoUrl || ''} brand={brand!} />
      <div className="customer-display-brand-names"><strong>{brand?.name}</strong></div>
    </div>}
    {!showingOrder ? <section className="customer-display-welcome">
      {knownBrand && <div className="customer-display-welcome-brand">
        <BrandMark key={brand?.logoUrl || ''} brand={brand!} />
        <strong>{brand?.name}</strong>
      </div>}
      <h1>{t(valid ? 'welcome' : 'invalidLink')}</h1>
      <p>{t(valid && knownBrand && snapshot?.state === 'idle' ? 'welcomeHelp' : 'waiting')}</p>
    </section> : <>
      <section className="customer-display-order">
        <div>
          <p className="customer-display-eyebrow">{t('yourOrder')}</p>
          <h1>{t(snapshot.state === 'order_error' ? 'orderErrorTitle' : snapshot.state === 'completed' ? (snapshot.payment.status === 'paid' ? 'paidTitle' : 'registeredTitle') : snapshot.state === 'payment_pending' ? 'processingTitle' : 'editingTitle')}</h1>
          <ul className="customer-display-lines">
            {snapshot.lines.map((line) => <li key={line.id}>
              <span className="customer-display-quantity">{line.quantity}</span>
              <div>
                <strong>{line.name}</strong>
                {line.variant && <p>{line.variant}</p>}
                {line.pricing && <>
                  <p className="customer-display-line-pricing">
                    {`${line.pricing.quantityLabel}${line.pricing.unit ? ` ${line.pricing.unit}` : ''} x ${money(line.pricing.unitPrice)}${line.pricing.unit ? `/${line.pricing.unit}` : ''}`}
                    {line.pricing.unitPriceBeforeDiscount !== undefined && <s>{`${money(line.pricing.unitPriceBeforeDiscount)}${line.pricing.unit ? `/${line.pricing.unit}` : ''}`}</s>}
                  </p>
                  {line.pricing.minimumQuantityLabel && <p className="customer-display-line-minimum">{t('minimumCharge', { quantity: line.pricing.minimumQuantityLabel, unit: line.pricing.unit })}</p>}
                </>}
                {line.kind !== 'product' && <small>{t(`lineKind.${line.kind}`)}</small>}
              </div>
              <strong>{money(line.amount)}</strong>
            </li>)}
          </ul>
        </div>
        <aside className="customer-display-totals">
          {breakdownKeys.map((key) => <div key={key}>
            <span>{t(key === 'taxes' && totals?.taxesIncluded ? 'taxesIncluded' : key)}</span><span>{money(totals?.[key] || 0)}</span>
          </div>)}
          {(['credit', 'loyalty'] as const).filter((key) => (totals?.[key] || 0) > 0).map((key) => <div key={key}><span>{t(key)}</span><span>{money(totals?.[key] || 0)}</span></div>)}
          <div className="customer-display-total"><span>{t('total')}</span><strong>{money(totals?.total || 0)}</strong></div>
          <p className={`customer-display-payment customer-display-payment-${snapshot.payment.status}`}>{t(snapshot.state === 'order_error' ? 'orderErrorHelp' : `payment.${snapshot.payment.status}`)}</p>
          {snapshot.state !== 'order_error' && snapshot.payment.amountPaid !== undefined && <div><span>{t('amountPaid')}</span><span>{money(snapshot.payment.amountPaid)}</span></div>}
          {snapshot.state !== 'order_error' && snapshot.payment.amountDue !== undefined && <div><span>{t('amountDue')}</span><span>{money(snapshot.payment.amountDue)}</span></div>}
        </aside>
      </section>
    </>}
    <footer className="customer-display-footer">{t('poweredBy')}</footer>
  </main>;
}

function BrandMark({ brand }: { brand: CustomerDisplayBrand }) {
  const [failed, setFailed] = useState(false);
  return brand.logoUrl && !failed
    ? <img className="customer-display-logo" src={brand.logoUrl} alt={brand.name} referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    : <span className="customer-display-monogram" aria-hidden="true">{getCustomerDisplayInitials(brand.name)}</span>;
}
